import { HdCircuitInfoVariant } from '../circuit-info/hd-circuit-info-variant';

/**
 * Beschrijft *wat* een doc-viewer-paneel (of één entry in zijn history-stack)
 * toont. Een discriminated union zodat {@link HdDocViewerComponent} per soort
 * de juiste content-component kan renderen:
 *  - `circuit`  → `<hd-circuit-info-full>` (rijk paneel met variant-switcher);
 *  - `overview` → `<hd-circuits-overview-info>` (algemeen README-overzicht);
 *  - `stream`   → markdown van de bewustzijnsstroom (`streams/<key>.md`);
 *  - `channels-overview` → `<hd-channels-overview-info>` (alle 36 kanalen per type);
 *  - `markdown` → een willekeurig markdown-asset op URL (fallback voor doorverwijzingen).
 *
 * Circuit- en stream-keys komen uit de bodygraph-metadata; de resolver
 * ({@link resolveDocLink}) vertaalt een markdown-link naar één van deze refs.
 */
export type HdDocRef =
  | { readonly kind: 'circuit'; readonly circuitKey: string; readonly variant: HdCircuitInfoVariant }
  | { readonly kind: 'overview' }
  | { readonly kind: 'stream'; readonly streamKey: string }
  | { readonly kind: 'channel'; readonly channelKey: string }
  | { readonly kind: 'channels-overview' }
  | { readonly kind: 'markdown'; readonly url: string; readonly title?: string };

/**
 * Stabiele paneel-key voor {@link FloatingPanelService.openOrFocus} (dedup
 * gebeurt op deze waarde). Twee klikken op dezelfde bron heropenen/focussen
 * dus hetzelfde gedockte paneel i.p.v. een tweede te openen.
 */
export function docPanelKey(ref: HdDocRef): string {
  switch (ref.kind) {
    case 'circuit':
      return `doc:circuit:${ref.circuitKey}`;
    case 'overview':
      return 'doc:overview';
    case 'stream':
      return `doc:stream:${ref.streamKey}`;
    case 'channel':
      return `doc:channel:${ref.channelKey}`;
    case 'channels-overview':
      return 'doc:channels-overview';
    case 'markdown':
      return `doc:md:${ref.url}`;
  }
}

/** Twee refs verwijzen naar dezelfde bron (voor history-dedup / focus-logica). */
export function isSameDocRef(a: HdDocRef, b: HdDocRef): boolean {
  return docPanelKey(a) === docPanelKey(b)
    && (a.kind !== 'circuit' || b.kind !== 'circuit' || a.variant === b.variant);
}
